import { post } from "@src/appV2/api/parsedApi";
import { type Money, MoneySchema } from "@src/appV2/lib/Money/types";
import { useMutation } from "@tanstack/react-query";
import { z } from "zod";

import { SHIFT_INVITE_PRICES_URL } from "../constants";

const shiftTypeSchema = z.enum(["am", "pm", "noc"]);
type ShiftType = z.infer<typeof shiftTypeSchema>;

const shiftInvitePriceRequestSchema = z.object({
  data: z.object({
    type: z.literal("shift-invite-price"),
    attributes: z.object({
      qualification: z.string(),
      workerId: z.string(),
      startAt: z.string(),
      endAt: z.string(),
      workplaceId: z.string(),
      shiftType: shiftTypeSchema,
      customRateBoost: MoneySchema.optional(),
    }),
  }),
});

const chargeConstraintsSchema = z.object({
  charge: z
    .object({
      minimumAmount: MoneySchema,
      maximumAmount: MoneySchema,
    })
    .optional(),
  adminCharge: z
    .object({
      minimumAmount: MoneySchema,
      maximumAmount: MoneySchema,
    })
    .optional(),
});

const chargeMemberSchema = z.object({
  label: z.string(),
  amount: MoneySchema,
});

const shiftInvitePriceResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal("shift-invite-price"),
    attributes: z.object({
      qualification: z.string(),
      pay: MoneySchema.optional(),
      charge: MoneySchema.optional(),
      createdAt: z.string(),
      constraints: chargeConstraintsSchema.optional(),
      chargeMembers: z.array(chargeMemberSchema).optional(),
    }),
  }),
});

export type ShiftInvitePriceResponse = z.infer<typeof shiftInvitePriceResponseSchema>;
export type ChargeMember = z.infer<typeof chargeMemberSchema>;

const SPARSE_FIELDSET_FIELDS = "qualification,charge,createdAt,constraints,chargeMembers";

interface CreateShiftInvitePriceParams {
  qualification: string;
  workerId: string;
  startAt: string;
  endAt: string;
  workplaceId: string;
  shiftType: ShiftType;
  customRateBoost?: Money;
}

export function useCreateShiftInvitePrice() {
  return useMutation({
    mutationFn: async (params: CreateShiftInvitePriceParams) => {
      const searchParams = new URLSearchParams();
      searchParams.append("fields[shift-invite-price]", SPARSE_FIELDSET_FIELDS);

      const response = await post({
        url: `${SHIFT_INVITE_PRICES_URL}?${searchParams.toString()}`,
        data: {
          data: {
            type: "shift-invite-price" as const,
            attributes: params,
          },
        },
        bodySchema: shiftInvitePriceRequestSchema,
        responseSchema: shiftInvitePriceResponseSchema,
      });

      return response.data;
    },
  });
}
